import React, { useState, useEffect } from 'react';
import { Search, Download, Trash2, Eye, CheckCircle, XCircle, ArrowLeft, ArrowRight, Filter, Mail, Phone, Building, User, Calendar, FileText, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMissionsPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [storedPassword, setStoredPassword] = useState('');
  
  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [submissionsPage, setSubmissionsPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [messagesPage, setMessagesPage] = useState(0);
  const [shareFilter, setShareFilter] = useState(null);
  
  // Detail view
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [submissionMessages, setSubmissionMessages] = useState([]);
  
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Test auth by fetching submissions
      await axios.get(`${API}/submissions/admin/submissions?password=${password}&limit=1`);
      setIsAuthenticated(true);
      setStoredPassword(password);
      localStorage.setItem('adminPassword', password);
      toast.success('Logged in successfully');
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  // Check for stored password on mount
  useEffect(() => {
    const stored = localStorage.getItem('adminPassword');
    if (stored) {
      setPassword(stored);
      setStoredPassword(stored);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch submissions
  const fetchSubmissions = async () => {
    if (!storedPassword) return;
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/submissions/admin/submissions`, {
        params: {
          password: storedPassword,
          search: searchQuery || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          skip: submissionsPage * 20,
          limit: 20
        }
      });
      
      setSubmissions(response.data.submissions);
      setTotalSubmissions(response.data.total);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('adminPassword');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages
  const fetchMessages = async () => {
    if (!storedPassword) return;
    
    try {
      const response = await axios.get(`${API}/submissions/admin/messages`, {
        params: {
          password: storedPassword,
          can_share: shareFilter,
          skip: messagesPage * 20,
          limit: 20
        }
      });
      
      setMessages(response.data.messages);
      setTotalMessages(response.data.total);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Fetch submission detail
  const fetchSubmissionDetail = async (id) => {
    if (!storedPassword) return;
    
    try {
      const response = await axios.get(`${API}/submissions/admin/submissions/${id}`, {
        params: { password: storedPassword }
      });
      
      setSelectedSubmission(response.data.submission);
      setSubmissionMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching submission detail:', error);
      toast.error('Failed to load submission details');
    }
  };

  // Delete submission
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      await axios.delete(`${API}/submissions/admin/submissions/${id}`, {
        params: { password: storedPassword }
      });
      
      toast.success('Submission deleted');
      fetchSubmissions();
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  // Mark as reviewed
  const handleMarkReviewed = async (id, reviewed) => {
    try {
      await axios.patch(`${API}/submissions/admin/submissions/${id}/reviewed`, null, {
        params: { password: storedPassword, reviewed }
      });
      
      toast.success(reviewed ? 'Marked as reviewed' : 'Marked as unreviewed');
      fetchSubmissions();
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(prev => ({ ...prev, reviewed }));
      }
    } catch (error) {
      console.error('Error updating submission:', error);
      toast.error('Failed to update submission');
    }
  };

  // Export CSV
  const handleExport = (type) => {
    window.open(`${API}/submissions/admin/export/${type}?password=${storedPassword}`, '_blank');
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubmissions();
      fetchMessages();
    }
  }, [isAuthenticated, submissionsPage, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMessages();
    }
  }, [messagesPage, shareFilter]);

  const handleLogout = () => {
    setIsAuthenticated(false);
    setStoredPassword('');
    setPassword('');
    localStorage.removeItem('adminPassword');
  };

  // Login form
  if (!isAuthenticated) {
    return (
      <main className="min-h-screen bg-[#0d0d0d] pt-32 pb-20 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-8">
            <h1 className="heading-2 text-[#f5f5f0] mb-6 text-center">Admin Access</h1>
            
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#f5f5f0]">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-[#0d0d0d] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] h-12"
                  placeholder="Enter admin password"
                />
              </div>
              
              <Button type="submit" disabled={loading} className="btn-primary w-full">
                {loading ? 'Verifying...' : 'Login'}
              </Button>
            </form>
          </div>
        </div>
      </main>
    );
  }

  // Detail view
  if (selectedSubmission) {
    return (
      <main className="min-h-screen bg-[#0d0d0d] pt-32 pb-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => setSelectedSubmission(null)}
              className="text-[#a8a8a0] hover:text-[#f5f5f0] mb-6"
            >
              <ArrowLeft size={18} className="mr-2" />
              Back to List
            </Button>

            <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-8 mb-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="heading-2 text-[#f5f5f0] mb-2">{selectedSubmission.name}</h1>
                  <p className="text-[#a8a8a0]">{selectedSubmission.title} at {selectedSubmission.organization}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={selectedSubmission.reviewed ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                    {selectedSubmission.reviewed ? 'Reviewed' : 'Pending'}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="flex items-center gap-3 text-[#a8a8a0]">
                  <Mail size={18} className="text-[#c9a86c]" />
                  <a href={`mailto:${selectedSubmission.email}`} className="hover:text-[#c9a86c]">
                    {selectedSubmission.email}
                  </a>
                </div>
                {selectedSubmission.phone && (
                  <div className="flex items-center gap-3 text-[#a8a8a0]">
                    <Phone size={18} className="text-[#c9a86c]" />
                    <a href={`tel:${selectedSubmission.phone}`} className="hover:text-[#c9a86c]">
                      {selectedSubmission.phone}
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-3 text-[#a8a8a0]">
                  <Calendar size={18} className="text-[#c9a86c]" />
                  {new Date(selectedSubmission.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <Label className="text-[#c9a86c] mb-2 block">Roles</Label>
                  <p className="text-[#f5f5f0]">{selectedSubmission.roles}</p>
                </div>
                <div>
                  <Label className="text-[#c9a86c] mb-2 block">What the World Needs</Label>
                  <p className="text-[#f5f5f0]">{selectedSubmission.world_need}</p>
                </div>
                <div>
                  <Label className="text-[#c9a86c] mb-2 block">Gift</Label>
                  <p className="text-[#f5f5f0]">{selectedSubmission.gift}</p>
                </div>
                <div className="bg-[#c9a86c]/10 border border-[#c9a86c]/30 p-6">
                  <Label className="text-[#c9a86c] mb-2 block">Final Mission Statement</Label>
                  <p className="text-xl font-display italic text-[#f5f5f0]">
                    "{selectedSubmission.final_mission_statement}"
                  </p>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <Button
                  onClick={() => handleMarkReviewed(selectedSubmission.id, !selectedSubmission.reviewed)}
                  className="btn-secondary"
                >
                  {selectedSubmission.reviewed ? (
                    <>
                      <XCircle size={18} className="mr-2" />
                      Mark Unreviewed
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} className="mr-2" />
                      Mark Reviewed
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleDelete(selectedSubmission.id)}
                  variant="destructive"
                  className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                >
                  <Trash2 size={18} className="mr-2" />
                  Delete
                </Button>
              </div>
            </div>

            {/* Messages for this submission */}
            {submissionMessages.length > 0 && (
              <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-8">
                <h3 className="heading-3 text-[#f5f5f0] mb-6">Messages ({submissionMessages.length})</h3>
                <div className="space-y-4">
                  {submissionMessages.map((msg) => (
                    <div key={msg.id} className="bg-[#0d0d0d] p-4 border border-[rgba(168,168,160,0.1)]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-[#a8a8a0]">
                          {new Date(msg.created_at).toLocaleDateString()}
                        </span>
                        {msg.can_share && (
                          <Badge className="bg-green-500/20 text-green-400">Can Share</Badge>
                        )}
                      </div>
                      <p className="text-[#f5f5f0]">{msg.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    );
  }

  // Main admin view
  return (
    <main className="min-h-screen bg-[#0d0d0d] pt-32 pb-20">
      <div className="container-custom">
        <div className="flex items-center justify-between mb-8">
          <h1 className="heading-2 text-[#f5f5f0]">Mission Submissions</h1>
          <Button onClick={handleLogout} variant="ghost" className="text-[#a8a8a0] hover:text-[#f5f5f0]">
            Logout
          </Button>
        </div>

        <Tabs defaultValue="submissions" className="space-y-6">
          <TabsList className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)]">
            <TabsTrigger value="submissions" className="data-[state=active]:bg-[#c9a86c] data-[state=active]:text-[#0d0d0d]">
              <FileText size={16} className="mr-2" />
              Submissions ({totalSubmissions})
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-[#c9a86c] data-[state=active]:text-[#0d0d0d]">
              <MessageSquare size={16} className="mr-2" />
              Messages ({totalMessages})
            </TabsTrigger>
          </TabsList>

          {/* Submissions Tab */}
          <TabsContent value="submissions" className="space-y-6">
            {/* Controls */}
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b6b65]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email, org..."
                  className="pl-10 bg-[#1a1a18] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] h-10"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => handleExport('submissions')} className="btn-secondary text-sm">
                  <Download size={16} className="mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>

            {/* Table */}
            <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[rgba(168,168,160,0.1)]">
                      <th className="text-left p-4 text-[#a8a8a0] text-sm font-medium">Date</th>
                      <th className="text-left p-4 text-[#a8a8a0] text-sm font-medium">Name</th>
                      <th className="text-left p-4 text-[#a8a8a0] text-sm font-medium">Email</th>
                      <th className="text-left p-4 text-[#a8a8a0] text-sm font-medium">Organization</th>
                      <th className="text-left p-4 text-[#a8a8a0] text-sm font-medium">Title</th>
                      <th className="text-left p-4 text-[#a8a8a0] text-sm font-medium">Status</th>
                      <th className="text-left p-4 text-[#a8a8a0] text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((sub) => (
                      <tr key={sub.id} className="border-b border-[rgba(168,168,160,0.05)] hover:bg-[#252522]">
                        <td className="p-4 text-[#a8a8a0] text-sm">
                          {new Date(sub.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4 text-[#f5f5f0]">{sub.name}</td>
                        <td className="p-4 text-[#a8a8a0] text-sm">{sub.email}</td>
                        <td className="p-4 text-[#a8a8a0] text-sm">{sub.organization}</td>
                        <td className="p-4 text-[#a8a8a0] text-sm">{sub.title}</td>
                        <td className="p-4">
                          <Badge className={sub.reviewed ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                            {sub.reviewed ? 'Reviewed' : 'Pending'}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => fetchSubmissionDetail(sub.id)}
                              className="text-[#a8a8a0] hover:text-[#c9a86c]"
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleMarkReviewed(sub.id, !sub.reviewed)}
                              className="text-[#a8a8a0] hover:text-[#c9a86c]"
                            >
                              {sub.reviewed ? <XCircle size={16} /> : <CheckCircle size={16} />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(sub.id)}
                              className="text-[#a8a8a0] hover:text-red-400"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {submissions.length === 0 && (
                <div className="p-12 text-center text-[#6b6b65]">
                  No submissions found
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalSubmissions > 20 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#a8a8a0]">
                  Showing {submissionsPage * 20 + 1} - {Math.min((submissionsPage + 1) * 20, totalSubmissions)} of {totalSubmissions}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={submissionsPage === 0}
                    onClick={() => setSubmissionsPage(p => p - 1)}
                    className="border-[rgba(168,168,160,0.2)] text-[#a8a8a0]"
                  >
                    <ArrowLeft size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={(submissionsPage + 1) * 20 >= totalSubmissions}
                    onClick={() => setSubmissionsPage(p => p + 1)}
                    className="border-[rgba(168,168,160,0.2)] text-[#a8a8a0]"
                  >
                    <ArrowRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={shareFilter === null ? "default" : "outline"}
                  onClick={() => setShareFilter(null)}
                  className={shareFilter === null ? "btn-primary" : "border-[rgba(168,168,160,0.2)] text-[#a8a8a0]"}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={shareFilter === true ? "default" : "outline"}
                  onClick={() => setShareFilter(true)}
                  className={shareFilter === true ? "btn-primary" : "border-[rgba(168,168,160,0.2)] text-[#a8a8a0]"}
                >
                  Can Share
                </Button>
              </div>
              <Button onClick={() => handleExport('messages')} className="btn-secondary text-sm">
                <Download size={16} className="mr-2" />
                Export CSV
              </Button>
            </div>

            <div className="space-y-4">
              {messages.map((msg) => (
                <div key={msg.id} className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-[#f5f5f0] font-medium">{msg.name}</p>
                      <p className="text-sm text-[#a8a8a0]">{msg.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {msg.can_share && (
                        <Badge className="bg-green-500/20 text-green-400">Can Share</Badge>
                      )}
                      <span className="text-sm text-[#6b6b65]">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <p className="text-[#f5f5f0]">{msg.message}</p>
                </div>
              ))}

              {messages.length === 0 && (
                <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-12 text-center text-[#6b6b65]">
                  No messages found
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

export default AdminMissionsPage;
